import apiClient from './client';
import {
  ApiCreateWorkoutResponse,
  ApiWorkout,
  CreateWorkoutPayload,
} from '@/stores/workoutPlayer/types';
import {
  getWeekDay,
  getWeekNumber,
  getWeekYear,
  nowMs,
} from '@/services/datetime';

type UpdateWorkoutPayload = {
  started_at: number | null;
  completed_at: number | null;
  completion_time: string | null;
  year: number;
  week: number;
  day: number;
};

type UpdateSegmentPayload = {
  has_skipped: boolean;
  started_at: number | null;
  resumed_at: number | null;
  completed_at: number | null;
  current_user_chapter_id: number | null;
};

function getDateParts() {
  return {
    year: getWeekYear(),
    week: getWeekNumber(),
    day: getWeekDay() || 7, // moment().day() returns 0 for Sunday; BE wants 7.
  };
}

function formatCompletionTime(startedAt: number, completedAt: number): string {
  const totalSeconds = Math.round((completedAt - startedAt) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export async function startWorkout(
  workoutId: number,
  startedAt: number,
): Promise<void> {
  const { year, week, day } = getDateParts();
  const payload: UpdateWorkoutPayload = {
    started_at: startedAt,
    completed_at: null,
    completion_time: null,
    year,
    week,
    day,
  };

  try {
    await apiClient.patch(`/user-workouts/${workoutId}`, payload);
    console.log('[API] Workout started:', workoutId);
  } catch (err) {
    console.error('[API] Failed to start workout:', err);
  }
}

export async function updateSegment(
  workoutId: number,
  segmentId: number,
  payload: Partial<UpdateSegmentPayload>,
): Promise<void> {
  const fullPayload: UpdateSegmentPayload = {
    has_skipped: false,
    started_at: null,
    resumed_at: null,
    completed_at: null,
    current_user_chapter_id: null,
    ...payload,
  };

  try {
    await apiClient.patch(
      `/user-workouts/${workoutId}/segments/${segmentId}`,
      fullPayload,
    );
    console.log('[API] Segment updated:', segmentId);
  } catch (err) {
    console.error('[API] Failed to update segment:', err);
  }
}

export async function completeWorkout(
  workoutId: number,
  startedAt: number,
  completedAt: number,
): Promise<void> {
  const { year, week, day } = getDateParts();
  const payload: UpdateWorkoutPayload = {
    started_at: startedAt,
    completed_at: completedAt,
    completion_time: formatCompletionTime(startedAt, completedAt),
    year,
    week,
    day,
  };

  try {
    await apiClient.patch(`/user-workouts/${workoutId}`, payload);
    console.log('[API] Workout completed:', workoutId);
  } catch (err) {
    console.error('[API] Failed to complete workout:', err);
  }
}

export async function createWorkout(
  payload: CreateWorkoutPayload,
): Promise<ApiCreateWorkoutResponse> {
  const response = await apiClient.post('/user-workouts', payload);
  return response.data;
}

/**
 * Re-PATCH an in-progress workout with the same `started_at` and `completed_at: null`
 * so the backend's update handler runs its "previously started" side effects:
 * recomputes `user_caloric_expenditures` for whatever segments have been
 * completed so far, refreshes weekly stats, and triggers the EARLY_ENDING
 * feedback path. The DB row itself doesn't change (same values), but the side
 * effects make abandoned workouts visible in the mobile Activities tab —
 * matching mobile, which flushes the same payload when the user ends early.
 *
 * Use on workout teardown when the user leaves the player without finishing.
 */
export async function flushWorkoutProgress(
  workoutId: number,
  startedAt: number,
  completedAt: number | null = null,
): Promise<void> {
  const { year, week, day } = getDateParts();
  try {
    await apiClient.patch(`/user-workouts/${workoutId}`, {
      started_at: startedAt,
      completed_at: completedAt,
      year,
      week,
      day,
    });
    console.log('[API] Workout progress flushed:', workoutId);
  } catch (err) {
    console.error('[API] Failed to flush workout progress:', err);
  }
}

export async function getWorkouts(
  year: number,
  week: number,
): Promise<ApiWorkout[]> {
  try {
    const response = await apiClient.get(
      `/user-workouts?year=${year}&week=${week}`,
    );
    const body = response.data;
    // Backend envelope: `{ success, data: { userWorkouts }, error }`.
    const workouts =
      body?.data?.userWorkouts ??
      body?.userWorkouts ??
      (Array.isArray(body) ? body : []);
    return workouts as ApiWorkout[];
  } catch (err: any) {
    console.error(
      '[API] Failed to fetch workouts:',
      err?.response?.status,
      err?.response?.data,
      err?.message,
    );
    return [];
  }
}

export async function skipUserWorkout(workoutId: number): Promise<void> {
  try {
    await apiClient.patch(`/user-workouts/${workoutId}/skip`, {
      skipped_at: nowMs(),
    });
    console.log('[API] Workout skipped:', workoutId);
  } catch (err) {
    console.error('[API] Failed to skip workout:', err);
  }
}

export async function updateUserSegmentChapter(
  workoutId: number,
  segmentId: number,
  chapterId: number,
  payload: { total_progress_time_ms: number | null },
): Promise<void> {
  try {
    await apiClient.patch(
      `/user-workouts/${workoutId}/segments/${segmentId}/user-segment-chapters/${chapterId}`,
      payload,
    );
    console.log('[API] Chapter updated:', chapterId);
  } catch (err) {
    console.error('[API] Failed to update chapter:', err);
  }
}
